import {
  dateCalendarFormatValidator,
  transformMultipleDateToCalendarFormat,
} from './helpers';
import {
  DatepickerError,
  LimitingDates,
  DatepickerMultipleDateFormat,
} from './types';

const patternSymbolsValidator = (value: string, pattern: RegExp) => {
  const unexpectedSymbols = value?.replace(new RegExp(pattern, 'gi'), '') || '';
  const uniqueUnexpectedStr = Array.from(new Set(unexpectedSymbols)).join('');
  if (uniqueUnexpectedStr) return uniqueUnexpectedStr;
};

const multipleEmptyPartsValidator = (date: DatepickerMultipleDateFormat) => {
  const { day, month, year } = date;
  const datePartsErrors: DatepickerError[] = [];
  if (!day) datePartsErrors.push({ type: 'emptyDay' });
  if (!month) datePartsErrors.push({ type: 'emptyMonth' });
  if (!year) datePartsErrors.push({ type: 'emptyYear' });

  return datePartsErrors.length ? datePartsErrors : undefined;
};

const commonInvalidPartsValidator = (
  date: string
): DatepickerError[] | undefined => {
  const originalDate = new Date(date);
  const originalDateParts = [
    originalDate.getFullYear(),
    originalDate.getMonth() + 1,
    originalDate.getDate(),
  ];
  const dateParts = date.split('-').map(Number);
  const [_, month, day] = dateParts;
  if (+month < 1 || +month > 12) return [{ type: 'invalidMonth' }];
  if (
    +day < 1 ||
    +day > 31 ||
    JSON.stringify(originalDateParts) !== JSON.stringify(dateParts)
  ) {
    return [{ type: 'invalidDay' }];
  }
};

const multipleFormatValidator = (
  date: DatepickerMultipleDateFormat
): DatepickerError[] | undefined => {
  const { day, month, year } = date;
  const yearFormat = /^\d{4}$/;
  if (!yearFormat.test(year)) return [{ type: 'invalidYear' }];
  const monthFormat = /^\d{1,2}$/;
  if (!monthFormat.test(month)) return [{ type: 'invalidMonth' }];
  const dayFormat = /^\d{1,2}$/;
  if (!dayFormat.test(day)) return [{ type: 'invalidDay' }];
};

const multipleExtraSymbolsValidator = (
  date: DatepickerMultipleDateFormat
): DatepickerError[] | undefined => {
  const { day, month, year } = date;
  let extraSymbols: string | undefined;
  const partWithExtraSymbols = Object.entries({ year, month, day }).find(
    ([_, value]) => {
      const partExtraSymbols = patternSymbolsValidator(value, /\d/);
      if (partExtraSymbols) {
        extraSymbols = partExtraSymbols;
        return true;
      }
    }
  );
  if (extraSymbols) {
    return [
      {
        type: 'extraSymbols',
        extraSymbols,
        part: partWithExtraSymbols![0] as keyof typeof date,
      },
    ];
  }
};

const commonUnavailableDateValidator = (
  date: string,
  { minDate, maxDate, disabledDates = [] }: LimitingDates
): DatepickerError[] | undefined => {
  const originalDateTime = new Date(date).getTime();

  if (minDate && new Date(minDate).getTime() > originalDateTime)
    return [{ type: 'minDate' }];
  if (maxDate && new Date(maxDate).getTime() < originalDateTime)
    return [{ type: 'maxDate' }];
  if (disabledDates.includes(date)) return [{ type: 'disabledDate' }];
};

const singleExtraSymbolsValidator = (
  date: string
): DatepickerError[] | undefined => {
  const extraSymbols = patternSymbolsValidator(date ?? '', /\d|-|\./);
  if (extraSymbols) return [{ type: 'extraSymbols', extraSymbols }];
};

const singleFormatValidator = (date: string): DatepickerError[] | undefined => {
  if (!dateCalendarFormatValidator(date)) return [{ type: 'format' }];
};

const multipleDefaultValidator = (
  date: DatepickerMultipleDateFormat,
  limitingDates?: LimitingDates
) =>
  multipleEmptyPartsValidator(date) ||
  multipleExtraSymbolsValidator(date) ||
  multipleFormatValidator(date) ||
  commonInvalidPartsValidator(transformMultipleDateToCalendarFormat(date)) ||
  (limitingDates &&
    commonUnavailableDateValidator(
      transformMultipleDateToCalendarFormat(date),
      limitingDates
    ));

const singleDefaultValidator = (date: string, limitingDates?: LimitingDates) =>
  singleExtraSymbolsValidator(date) ||
  singleFormatValidator(date) ||
  commonInvalidPartsValidator(date) ||
  (limitingDates && commonUnavailableDateValidator(date, limitingDates));

export const datepickerValidator = {
  /**
   * Алгоритм проверки даты в датапикере с несколькими полями:
   * - Проверяем на пустоту
   * - Проверяем на лишние символы
   * - Проверяем на формат ГГГГ-ММ-ДД
   * - Проверяем на валидность даты
   * - Проверяем на доступность даты ориентируясь на minDate, maxDate, disabledDates
   */
  multiple: {
    formatValidator: multipleFormatValidator,
    emptyPartsValidator: multipleEmptyPartsValidator,
    invalidPartsValidator: commonInvalidPartsValidator,
    extraSymbolsValidator: multipleExtraSymbolsValidator,
    unavailableDateValidator: commonUnavailableDateValidator,
    /**
     * Реализует стандартную валидацию даты согласно алгоритму в датапикере с несколькими полями
     */
    defaultValidator: multipleDefaultValidator,
  },
  /**
   * Алгоритм проверки даты в датапикере с одним полем:
   * - Проверяем на лишние символы
   * - Проверяем на формат ГГГГ-ММ-ДД
   * - Проверяем на валидность даты
   * - Проверяем на доступность даты ориентируясь на minDate, maxDate, disabledDates
   */
  single: {
    extraSymbolsValidator: singleExtraSymbolsValidator,
    formatValidator: singleFormatValidator,
    invalidPartsValidator: commonInvalidPartsValidator,
    unavailableDateValidator: commonUnavailableDateValidator,
    /**
     * Реализует стандартную валидацию даты согласно алгоритму в датапикере с одним полем
     */
    defaultValidator: singleDefaultValidator,
  },
};
